import { BaseUser, baseUserSchema, User } from './users';
import { Pack } from './packs';
import { z } from 'zod';
import { allTags } from './tags';
import { LevelResolvedRecord } from './submissions';
import { nlwTiers } from './nlw';

export const levelStatuses = [
    'Pending',
    'MainList',
    'Legacy',
    'Removed',
] as const;
export type LevelStatus = (typeof levelStatuses)[number];

export const baseLevelSchema = z
    .object({
        id: z.uuidv4(),
        name: z.string(),
        position: z.number().nullable(),
        level_id: z.number(),
        two_player: z.boolean(),
        points: z.number(),
        status: z.enum(levelStatuses),
        requires_raw_footage: z.boolean(),
    })
    .superRefine((data, ctx) => {
        if (
            (data.status == 'MainList' || data.status == 'Legacy') &&
            data.position === null
        )
            ctx.addIssue({
                path: ['position'],
                code: 'invalid_type',
                expected: 'number',
                received: 'null',
            });
        if (
            (data.status == 'Pending' || data.status == 'Removed') &&
            data.position !== null
        )
            ctx.addIssue({
                path: ['position'],
                code: 'invalid_type',
                expected: 'null',
                received: 'number',
            });
    });

export type BaseLevel = z.infer<typeof baseLevelSchema>;

export type PlacedBaseLevel = BaseLevel & {
    position: NonNullable<BaseLevel['position']>;
};

export interface ProfileMergedLevel extends BaseLevel {
    published: boolean;
    created: boolean;
}

export const levelSchema = baseLevelSchema.extend({
    description: z.string().optional(),
    publisher: baseUserSchema,
    edel_enjoyment: z.number().optional(),
    is_edel_pending: z.boolean().optional(),
    gddl_tier: z.number().optional(),
    nlw_tier: z.enum(nlwTiers).optional(),
    tags: z.array(z.enum(allTags)).optional(),
    song: z.number().optional(),
    completed_by_user: z.boolean().optional(),
});

export type Level = z.infer<typeof levelSchema>;

export type PlacedLevel = Level & {
    position: NonNullable<Level['position']>;
};

export type LevelWithExtraFilters = Level & {
    is_favorite: boolean;
    records?: number;
};

export interface ResolvedLevel extends Level {
    publisher: User;
    verifications: LevelResolvedRecord[];
}

export interface CountryClanProfilePublishedLevel extends BaseLevel {
    publisher: User;
}

export interface CountryClanProfileCreatedLevel extends BaseLevel {
    creators: BaseUser[];
}

export interface CountryClanProfileMergedLevel extends BaseLevel {
    publisher: User | null;
    creators: BaseUser[];
}

export interface IDLLevel {
    id: string;
    legacy: boolean;
    name: string;
    packs: Array<Pack>;
    position: number;
    publisher: User;
    formerRank: number;
}

export const levelCustomCopyStatuses = [
    'Published',
    'Allowed',
    'Banned',
] as const;
export type LevelCustomCopyStatus = (typeof levelCustomCopyStatuses)[number];

export const levelCustomCopyTypes = [
    'Bugfix',
    'Ldm',
    'GlobedCopy',
    'Other',
] as const;
export type LevelCustomCopyType = (typeof levelCustomCopyTypes)[number];

export const levelCustomCopySchema = z.object({
    id: z.uuidv4(),
    level_id: z.string(),
    copy_id: z.number(),
    id_type: z.enum(levelCustomCopyTypes),
    status: z.enum(levelCustomCopyStatuses),
    added_by: baseUserSchema,
    description: z.string().optional(),
});

export type LevelCustomCopy = z.infer<typeof levelCustomCopySchema>;

export const levelNoteTypes = [
    'ReviewerNotes',
    'PublicNotes',
    'Other',
] as const;
export type LevelNoteType = (typeof levelNoteTypes)[number];

export const levelNoteSchema = z.object({
    id: z.uuidv4(),
    level_id: z.string(),
    note: z.string(),
    note_type: z.enum(levelNoteTypes),
    timestamp: z.string().nullable(),
    added_by: baseUserSchema,
    created_at: z.string(),
});

export type LevelNote = z.infer<typeof levelNoteSchema>;

export const levelUpdateTypes = [
    'Buff',
    'Nerf',
    'Balance',
    'BugFix',
    'Other',
] as const;
export type LevelUpdateType = (typeof levelUpdateTypes)[number];

export const levelUpdateSchema = z.object({
    id: z.uuidv4(),
    level_id: z.string(),
    changelog: z.string().nullable(),
    update_type: z.enum(levelUpdateTypes),
    timestamp: z.string(),
    created_at: z.string(),
});

export type LevelUpdate = z.infer<typeof levelUpdateSchema>;
